import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@shared/components/ui/button';
import { Card, CardContent } from '@shared/components/ui/card';
import { Badge } from '@shared/components/ui/badge';
import { Search, Clock, Newspaper, ArrowRight, Calendar, Building, Tags, BookOpen, Loader2 } from 'lucide-react';
import { jurisprudentieService } from '@shared/services/jurisprudentie.service';
import { formatDate } from '@shared/lib/utils/format';
import { useAuth } from '@shared/features/auth/context/AuthContext';
import { usePortalConfig, usePortalIds } from '@shared/config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Helper to extract rechtsvraag from JSON summary
function getRechtsvraag(summary: string): string | null {
  try {
    const parsed: unknown = JSON.parse(summary);
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      const rechtsvraagKey = Object.keys(obj).find(
        key => key.toLowerCase() === 'rechtsvraag'
      );
      if (rechtsvraagKey && typeof obj[rechtsvraagKey] === 'string') {
        return obj[rechtsvraagKey];
      }
    }
  } catch {
    // Not JSON
  }
  return null;
}

// Component for the latest case preview card
function LatestCasePreview() {
  const portalIds = usePortalIds();

  const { data: latestCase, isLoading, error } = useQuery({
    queryKey: ['jurisprudentie', 'public-latest', portalIds],
    queryFn: () => jurisprudentieService.getPublicLatestCase(portalIds),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[280px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary/40" />
      </div>
    );
  }

  if (error || !latestCase) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[280px] text-center px-4">
        <Search className="h-16 w-16 text-primary/40 mx-auto mb-2" />
        <p className="text-muted-foreground font-medium">Zoek direct in meer dan 10.000+ uitspraken</p>
      </div>
    );
  }

  const rechtsvraag = getRechtsvraag(latestCase.samenvatting);
  const MAX_VISIBLE_BADGES = 3;

  // Combine themas and wetsartikelen into badges
  const allBadges = [
    ...(latestCase.themas || []).map((thema, index) => (
      <Badge
        key={`thema-${index}`}
        className="bg-primary/10 text-primary border-primary/20 text-xs"
      >
        <Tags className="h-3 w-3 mr-1" />
        {thema}
      </Badge>
    )),
    ...(latestCase.wetsartikelen || []).map((wetsartikel, index) => (
      <Badge
        key={`wetsartikel-${index}`}
        className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-xs"
      >
        <BookOpen className="h-3 w-3 mr-1" />
        {wetsartikel}
      </Badge>
    ))
  ];

  const visibleBadges = allBadges.slice(0, MAX_VISIBLE_BADGES);
  const remainingCount = allBadges.length - MAX_VISIBLE_BADGES;

  return (
    <div className="p-4 h-full flex flex-col">
      {/* Header label */}
      <div className="flex items-center gap-2 mb-3">
        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Meest recente uitspraak
        </span>
      </div>

      {/* Title */}
      <h3 className="text-base font-semibold text-foreground leading-tight mb-3 line-clamp-2">
        {latestCase.titel}
      </h3>

      {/* Metadata */}
      <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-xs text-muted-foreground font-medium mb-3">
        <span className="bg-muted px-2 py-1 rounded font-mono tracking-wide border border-border text-[10px]">
          {latestCase.ecli}
        </span>
        <span className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          Gepubliceerd {formatDate(latestCase.publicatie_datum)}
        </span>
        {latestCase.instantie_naam && (
          <span className="flex items-center gap-1">
            <Building className="h-3.5 w-3.5" />
            {latestCase.instantie_naam}
          </span>
        )}
      </div>

      {/* Summary/Rechtsvraag */}
      {rechtsvraag ? (
        <div className="bg-muted/50 dark:bg-muted/30 rounded-lg p-3 border border-border/50 mb-3 flex-grow">
          <p className="text-sm leading-relaxed line-clamp-3 text-muted-foreground">
            <span className="font-semibold block mb-1 text-foreground">Rechtsvraag:</span>
            {rechtsvraag}
          </p>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground line-clamp-3 mb-3 flex-grow">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {latestCase.samenvatting}
          </ReactMarkdown>
        </div>
      )}

      {/* Badges */}
      {allBadges.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-auto">
          {visibleBadges}
          {remainingCount > 0 && (
            <Badge
              variant="outline"
              className="text-xs bg-muted/50 text-muted-foreground border-border"
            >
              +{remainingCount} meer
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const { user } = useAuth();
  const { branding, id } = usePortalConfig();
  const isLexlabour = id === 'lexlabour';
  const isLoggedIn = !!user;

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative pt-12 pb-20 lg:pt-24 lg:pb-32 overflow-hidden bg-background">
        {/* Dotted pattern background */}
        <div className="absolute inset-0 hero-pattern opacity-60 pointer-events-none" />

        {/* Decorative blur circles */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-primary/10 dark:bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight mb-6 text-foreground">
            <span className="text-primary">
              Dagelijkse up to date{' '}
            </span>
            <br className="hidden md:block" />
            van alle {branding.rechtsgebiedNaam} uitspraken{' '}
          </h1>

          <p className="mt-4 max-w-2xl mx-auto text-xl text-muted-foreground leading-relaxed">
            {branding.rechtsgebiedBeschrijving}
          </p>

          <div className="mt-10 flex justify-center gap-4 flex-col sm:flex-row">
            <Link to="/register">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-xl shadow-xl shadow-primary/25 hover:-translate-y-1 transition-all motion-reduce:hover:transform-none"
              >
                Maak gratis account aan
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-xl hover:shadow-lg transition-all text-foreground"
              >
                Inloggen
              </Button>
            </Link>
          </div>

          {/* Latest case preview */}
          <div className="mt-16 relative mx-auto max-w-3xl">
            <Link to={isLoggedIn ? "/search" : "/register"} className="block">
              <div className="rounded-2xl bg-card border border-border shadow-2xl p-2 md:p-4 hover:shadow-3xl hover:border-primary/30 transition-all duration-300 cursor-pointer group">
                <div className="bg-muted/30 rounded-xl overflow-hidden relative">
                  <LatestCasePreview />
                </div>
                {/* Hover overlay with CTA */}
                <div className="absolute inset-0 bg-primary/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                  <span className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium shadow-lg">
                    {isLoggedIn ? "Zoek in alle uitspraken →" : "Bekijk alle uitspraken →"}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background relative">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4 text-foreground">Waarom {branding.name}?</h2>
            <p className="text-muted-foreground">Ontworpen voor juristen die efficiëntie en volledigheid eisen.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="group rounded-2xl border-border/50 shadow-sm hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 motion-reduce:hover:shadow-sm feature-card dark:border-border">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform motion-reduce:group-hover:scale-100">
                  <Newspaper className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-foreground">Makkelijk up-to-date</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Met onze dagelijkse nieuwsbrief met samenvattingen van de uitspraken van die dag blijft u makkelijk en snel up to date.
                </p>
              </CardContent>
            </Card>

            <Card className="group rounded-2xl border-border/50 shadow-sm hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 motion-reduce:hover:shadow-sm feature-card dark:border-border">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform motion-reduce:group-hover:scale-100">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-foreground">Volledig & actueel</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Toegang tot een dagelijks bijgewerkte database van alle uitspraken en hun samenvatting, direct beschikbaar.
                </p>
              </CardContent>
            </Card>

            <Card className="group rounded-2xl border-border/50 shadow-sm hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 motion-reduce:hover:shadow-sm feature-card dark:border-border">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform motion-reduce:group-hover:scale-100">
                  <Search className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-foreground">Vind snel de juiste casus</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Alle uitspraken zijn gelabeld op onderwerp en wetsartikel, zodat u in seconden vindt wat u zoekt.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-background border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-border">
            <div className="p-4">
              <div className="text-4xl lg:text-5xl font-extrabold text-primary mb-2">10.000+</div>
              <div className="text-lg font-medium text-foreground">Uitspraken</div>
              <div className="text-sm text-muted-foreground mt-1">Direct doorzoekbaar</div>
            </div>
            <div className="p-4">
              <div className="text-4xl lg:text-5xl font-extrabold text-primary mb-2">Dagelijks</div>
              <div className="text-lg font-medium text-foreground">Bijgewerkt</div>
              <div className="text-sm text-muted-foreground mt-1">Altijd actueel</div>
            </div>
            <div className="p-4">
              <div className="text-4xl lg:text-5xl font-extrabold text-primary mb-2">Custom</div>
              <div className="text-lg font-medium text-foreground">Labels</div>
              <div className="text-sm text-muted-foreground mt-1">Slimme categorisatie</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-900 dark:bg-black">
          <div className={`absolute inset-0 opacity-20 [background-size:24px_24px] ${isLexlabour ? 'bg-[radial-gradient(#F59E0B_1px,transparent_1px)]' : id === 'lexzone' ? 'bg-[radial-gradient(#00824a_1px,transparent_1px)]' : 'bg-[radial-gradient(#1e40af_1px,transparent_1px)]'}`} />
        </div>
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Klaar om te beginnen?</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Krijg direct toegang tot de meest complete juridische database van Nederland en bespaar kostbare tijd.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-xl shadow-lg shadow-primary/30 hover:scale-105 transition-all motion-reduce:hover:scale-100"
              >
                Maak een account aan
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-xl border-slate-600 text-white hover:bg-slate-800 bg-transparent"
              >
                Neem contact op
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
